import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";

import { directFetchData } from "../services/api.services";

// Create context
const AppContext = createContext();

// Constants
const CACHE_KEY = "appContextData";
const CACHE_TTL = 1000 * 60 * 10; // 10 menit

// Endpoint list
const endpoints = {
  landingpage: "landing-page",
  service: "service",
  brand: "brand",
  portofolio: "portfolio",
  contact: "contact",
  about: "pages/who-we-are",
  home: "pages/home",
  blog: "blog",
  product: "product",
  productType: "product-type",
  benefit: "benefit",
  serviceBanner: "service/banner",
  project: "project",
  storyline: "storyline",
  menu: "menu",
  social: "social",
  integrationImg: "integration-image",
};

// AppProvider component
export const AppProvider = ({ children }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);

  const fetchedRef = useRef(false);

  /**
   * Customize button Rentetan berdasarkan
   * nomor WhatsApp aktif dari API.
   */
  const customizeRentetanWhatsapp = (socialData) => {
    const whatsapp = socialData?.find(
      (item) => item.name?.toLowerCase() === "whatsapp"
    );

    if (!whatsapp) {
      console.log("WhatsApp social media tidak ditemukan.");
      return;
    }

    /**
     * Kalau number_id tidak ada,
     * berarti gunakan nomor default Rentetan.
     *
     * Jangan ubah button.
     */
    if (!whatsapp.number_id) {
      console.log(
        "Rentetan menggunakan nomor WhatsApp default."
      );

      return;
    }

    /**
     * Update button Rentetan.
     */
    const updateButton = () => {
      const button = document.querySelector("#btn-wa");

      if (!button) {
        return false;
      }

      // ID button mengikuti ID nomor WhatsApp
      button.id = `btn-wa${whatsapp.number_id}`;

      // URL dari API sudah merupakan URL final
      button.href = whatsapp.url;

      console.log(
        `Rentetan menggunakan WhatsApp custom #${whatsapp.number_id}`
      );

      return true;
    };

    /**
     * Kalau button sudah dibuat oleh Rentetan,
     * langsung update.
     */
    if (updateButton()) {
      return;
    }

    /**
     * Kalau Rentetan belum membuat button,
     * tunggu sampai button muncul.
     */
    const observer = new MutationObserver(() => {
      if (updateButton()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    /**
     * Safety:
     * jangan biarkan observer hidup selamanya.
     */
    setTimeout(() => {
      observer.disconnect();
    }, 10000);
  };

  useEffect(() => {
    if (fetchedRef.current) return;

    fetchedRef.current = true;

    const fetchAll = async () => {
      try {
        let cachedData = null;

        // =====================================================
        // 1. CHECK CACHE
        // =====================================================

        const cached = localStorage.getItem(CACHE_KEY);

        if (cached) {
          const parsed = JSON.parse(cached);

          const isExpired =
            Date.now() - parsed.timestamp > CACHE_TTL;

          if (!isExpired) {
            cachedData = parsed.data;

            console.log("✅ Menggunakan data dari cache.");

            setData(cachedData);
            setIsFetched(true);
          } else {
            console.log(
              "🕒 Cache expired, fetching new data..."
            );
          }
        }

        // =====================================================
        // 2. FETCH DATA
        // =====================================================

        /**
         * Kalau cache masih valid:
         * fetch hanya social supaya nomor WhatsApp
         * selalu mengikuti setting terbaru.
         *
         * Kalau cache tidak ada / expired:
         * fetch semuanya.
         */
        const entriesToFetch = cachedData
          ? [["social", endpoints.social]]
          : Object.entries(endpoints);

        const results = await Promise.all(
          entriesToFetch.map(async ([key, endpoint]) => {
            try {
              const response = await directFetchData(endpoint);

              if (
                key === "home" ||
                key === "about" ||
                key === "project"
              ) {
                return [key, response];
              }

              return [key, response.data];
            } catch (err) {
              console.warn(
                `⚠️ Gagal fetch ${endpoint}:`,
                err.message
              );

              return [key, null];
            }
          })
        );

        const freshData = Object.fromEntries(results);

        // =====================================================
        // 3. MERGE DATA
        // =====================================================

        const resultObject = cachedData
          ? {
              ...cachedData,
              social: freshData.social,
            }
          : freshData;

        setData(resultObject);
        setIsFetched(true);

        // =====================================================
        // 4. CUSTOMIZE RENTETAN WHATSAPP
        // =====================================================

        if (resultObject.social) {
          customizeRentetanWhatsapp(resultObject.social);
        }

        // =====================================================
        // 5. SAVE CACHE
        // =====================================================

        /**
         * Hanya update cache kalau sebelumnya
         * memang tidak ada cache / cache sudah expired.
         *
         * Jangan overwrite cache setiap kali fetch social.
         */
        if (!cachedData) {
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
              timestamp: Date.now(),
              data: resultObject,
            })
          );
        }
      } catch (err) {
        console.error(
          "❌ Error fetching all API:",
          err
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Value context
  const value = useMemo(
    () => ({
      ...data,
      isFetched,
      loading,
    }),
    [data, isFetched, loading]
  );

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook
export const useAppContext = () =>
  useContext(AppContext);